import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { MiloPictureAction, MiloStreamEvent, MiloUnavailableReason } from '@/enums/milo'
import { MiloError, miloErrorOf, miloErrorText } from '@/libs/miloChat'
import type { MiloState } from '@/libs/milo/milo'
import { miloPageOf } from '@/libs/miloPage'
import router from '@/router'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import { useTenantStore } from '@/stores/tenant'
import type {
    IMiloDone,
    IMiloMessage,
    IMiloProposal,
    IMiloProposalResult,
    IMiloStart,
    IMiloStatus,
    IMiloStreamError,
    IMiloSuggestion,
} from '@/types/Milo'

/*
 * MILO IN THE CONSOLE (docs/API.md §14, surface `console`): the conversation of the signed-in
 * person in the tenant being worked in (`tenantStore.contextTid`), kept here rather than in the
 * drawer so closing and opening it keeps the thread and the figure's state.
 *
 * A message is POSTed with `Accept: text/event-stream` and read as it is written
 * (`fetchStore.stream`): `start` adds the person's message, `avatar` drives the figure, `delta`
 * grows the reply, `proposal` shows a card at once, `done` replaces the growing reply with the
 * stored one and brings the follow-up chips, `error` drops what was streamed (§14.5).
 */

/** How long a one-shot gesture (laugh, wink) and the greeting stay before the next state. */
const GESTURE_MS = 1400
const GREET_MS   = 2200

export const useMiloStore = defineStore('milo', () => {
    const appStore    = useAppStore()
    const fetchStore  = useFetchStore()
    const tenantStore = useTenantStore()

    const open        = ref(false)
    const everOpened  = ref(false)
    const status      = ref<IMiloStatus | null>(null)
    const loadedTid   = ref('')
    const messages    = ref<IMiloMessage[]>([])
    const suggestions = ref<IMiloSuggestion[]>([])
    const sending     = ref(false)
    /** The reply being written: its text so far and the proposals that arrived with it. */
    const live        = ref<{ id: string, text: string, proposals: IMiloProposal[], activity: string } | null>(null)
    const failure     = ref<{ code: number, text: string } | null>(null)
    const leftToday   = ref<number | null>(null)
    const turnsLeft   = ref<number | null>(null)
    const avatar      = ref<MiloState>('idle')
    /** Proposals being confirmed or cancelled, by id. */
    const deciding    = ref<Record<string, boolean>>({})
    /** A local event proposal's picture action under way, by id (§14.6.1). */
    const picturing   = ref<Record<string, string>>({})

    const tid = computed((): string => tenantStore.contextTid)

    const base = (): string => `/tenants/${tid.value}/milo`

    /** Whether the top bar offers Milo at all: switched off for the console hides it. */
    const offered = computed((): boolean => !!status.value && (status.value.available
        || (status.value.reason === MiloUnavailableReason.NotConfigured && appStore.isProviderAdmin())))

    // ── the figure ──────────────────────────────────────────────────────────────

    let gestureUntil = 0
    let pendingState: MiloState | null = null
    let gestureTimer: ReturnType<typeof setTimeout> | undefined

    /** A state from the stream; a laugh or a wink (and the greeting) is let finish first. */
    const setAvatar = (state: MiloState, hold = 0) => {
        const now = Date.now()

        if (now < gestureUntil && !hold) {
            pendingState = state

            return
        }

        if (gestureTimer) clearTimeout(gestureTimer)
        avatar.value = state
        pendingState = null

        const oneShot = hold || (state === 'laugh' || state === 'wink' ? GESTURE_MS : 0)

        if (!oneShot) {
            gestureUntil = 0

            return
        }

        gestureUntil = now + oneShot
        gestureTimer = setTimeout(() => {
            gestureUntil = 0
            avatar.value = pendingState ?? (sending.value ? 'think' : 'idle')
            pendingState = null
        }, oneShot)
    }

    // ── reading ─────────────────────────────────────────────────────────────────

    const fill = (value: IMiloStatus) => {
        status.value      = value
        messages.value    = value.conversation?.messages ?? []
        suggestions.value = value.suggestions ?? []
        leftToday.value   = value.limits?.left_today ?? null
        turnsLeft.value   = value.limits?.turns_left ?? null
    }

    const load = async () => {
        const id = tid.value

        if (!id) {
            status.value = null

            return
        }

        const { statusCode, data } = await fetchStore.fetch(`${base()}?lang=${appStore.locale}`).get().json<IMiloStatus>()

        if (id !== tid.value) return

        loadedTid.value = id

        if (statusCode.value === 200 && data.value) fill(data.value)
        else status.value = null
    }

    let controller: AbortController | null = null

    // A new tenant is another conversation; a new console language another greeting.
    watch([tid, () => appStore.locale, () => appStore.userData?.id], () => {
        // An answer still streaming belongs to the old tenant's thread: stopped, not appended here.
        controller?.abort()
        controller    = null
        live.value    = null
        failure.value = null
        if (appStore.isLoggedIn() && appStore.userData) void load()
        else status.value = null
    }, { immediate: true })

    // ── the drawer ──────────────────────────────────────────────────────────────

    const show = () => {
        open.value       = true
        everOpened.value = true
        if (!sending.value) setAvatar('greet', GREET_MS)
        // The status read as the drawer opens: the limits and a thread the phone of another tab moved on.
        void load()
    }

    const hide = () => {
        open.value = false
    }

    const toggle = () => (open.value ? hide() : show())

    // ── writing ─────────────────────────────────────────────────────────────────


    const send = async (text: string, suggestionId = '') => {
        const body = text.trim()

        if (!body || sending.value || !tid.value) return

        sending.value = true
        failure.value = null
        live.value    = { id: '', text: '', proposals: [], activity: '' }
        setAvatar('think')
        controller = new AbortController()

        let finished = false

        const { status: code, data } = await fetchStore.stream(`${base()}/messages`, {
            text:          body,
            lang:          appStore.locale,
            suggestion_id: suggestionId,
            // Where the person is now — the drawer stays open across pages (§14.3 `page`).
            page:          miloPageOf(router.currentRoute.value),
        }, (name, payload) => {
            switch (name) {
                case MiloStreamEvent.Start: {
                    const start = payload as IMiloStart

                    messages.value = [...messages.value, start.user_message]
                    if (live.value) live.value.id = start.message_id
                    break
                }
                case MiloStreamEvent.Avatar:
                    setAvatar(String((payload as { state?: string }).state ?? 'idle') as MiloState)
                    break
                case MiloStreamEvent.Activity:
                    if (live.value) live.value.activity = String((payload as { tool?: string }).tool ?? '')
                    break
                case MiloStreamEvent.Delta:
                    if (live.value) {
                        live.value.text    += String((payload as { text?: string }).text ?? '')
                        live.value.activity = ''
                    }
                    break
                case MiloStreamEvent.Retract:
                    // Words before a lookup were not the answer (§14.5): the reply so far instead.
                    if (live.value) live.value.text = String((payload as { text?: string }).text ?? '')
                    break
                case MiloStreamEvent.Proposal:
                    if (live.value) live.value.proposals = [...live.value.proposals, payload as IMiloProposal]
                    break
                case MiloStreamEvent.Done: {
                    const done = payload as IMiloDone

                    messages.value    = [...messages.value, done.message]
                    suggestions.value = done.suggestions ?? []
                    leftToday.value   = done.left_today
                    turnsLeft.value   = done.turns_left
                    live.value        = null
                    finished          = true
                    break
                }
                case MiloStreamEvent.Error: {
                    const error = payload as IMiloStreamError

                    failure.value = { code: error.code, text: miloErrorText(error.code, error.details) }
                    live.value    = null
                    finished      = true
                    break
                }
                default:
                    break
            }
        }, controller.signal)

        controller    = null
        sending.value = false

        if (code !== 200) {
            live.value    = null
            failure.value = code === 499 ? null : miloErrorOf(data)
            if (!failure.value?.code && code !== 499) failure.value = { code: 0, text: miloErrorText(0) }
            setAvatar('idle')

            return
        }

        // The stream ended without `done` or `error` (a proxy cut it): say so, keep the thread.
        if (!finished) {
            live.value    = null
            failure.value = { code: MiloError.Failed, text: miloErrorText(MiloError.Failed) }
        }

        setAvatar('idle')
    }

    /** Puts a proposal's new state into the message that carries it. */
    const replace = (next: IMiloProposal) => {
        messages.value = messages.value.map((message) => message.proposals?.some((item) => item.id === next.id)
            ? { ...message, proposals: message.proposals.map((item) => item.id === next.id ? next : item) }
            : message)
    }

    const decide = async (proposal: IMiloProposal, confirm: boolean) => {
        if (deciding.value[proposal.id]) return

        deciding.value = { ...deciding.value, [proposal.id]: true }

        const path = `${base()}/proposals/${confirm ? 'confirm' : 'cancel'}`
        const { statusCode, data } = await fetchStore.fetch(path).post({ token: proposal.token }).json<IMiloProposalResult | IMiloProposal>()

        deciding.value = { ...deciding.value, [proposal.id]: false }

        if (statusCode.value === 200 && data.value) {
            if (confirm) {
                const result = data.value as IMiloProposalResult

                replace(result.proposal)
                if (result.message) messages.value = [...messages.value, result.message]
            } else {
                replace(data.value as IMiloProposal)
            }

            return
        }

        failure.value = miloErrorOf(data.value)

        // No longer pending (51): read the thread again for its real state.
        if (failure.value.code === MiloError.ProposalClosed) void load()
    }

    /**
     * A local event proposal's picture removed, switched or made again before Potrdi (§14.6.1);
     * the proposal stays pending and comes back with its new details. An AI picture takes a while.
     */
    const picture = async (proposal: IMiloProposal, action: MiloPictureAction) => {
        if (picturing.value[proposal.id] || deciding.value[proposal.id]) return

        picturing.value = { ...picturing.value, [proposal.id]: action }

        const { statusCode, data } = await fetchStore.fetch(`${base()}/proposals/picture`).post({ token: proposal.token, action }).json<IMiloProposal>()

        picturing.value = { ...picturing.value, [proposal.id]: '' }

        if (statusCode.value === 200 && data.value) {
            replace(data.value)

            return
        }

        failure.value = miloErrorOf(data.value)

        if (failure.value.code === MiloError.ProposalClosed) void load()
    }

    const startNew = async () => {
        controller?.abort()

        const { statusCode } = await fetchStore.fetch(`${base()}/conversation`).delete().json()

        if (statusCode.value === 204 || statusCode.value === 200) {
            messages.value = []
            failure.value  = null
            live.value     = null
            setAvatar('greet', GREET_MS)
            await load()
        }
    }

    return {
        open, everOpened, status, loadedTid, messages, suggestions, sending, live, failure, leftToday, turnsLeft,
        avatar, deciding, picturing, offered, tid,
        load, show, hide, toggle, send, decide, picture, startNew,
    }
})
